import { usePostHog } from '@posthog/react';
import InvoicePostponeFormModal from 'components/modals/invoices/InvoicePostponeFormModal';
import InvoiceWorkflowRejectModal from 'components/modals/invoices/InvoiceWorkflowRejectFormModal';
import { useSendInvoicesModal } from 'components/modals/invoices/SendInvoices';
import { useModal } from 'components/modals/useModal';
import { useCallback } from 'react';
import { UseFormHandleSubmit } from 'react-hook-form';
import { useUpdateInvoiceStatusMutation } from 'store/api/invoices';
import {
    InvoiceData,
    InvoiceEmailTemplateType,
    InvoiceForm,
    InvoiceStatusType,
    InvoiceTransactionActionType,
} from 'types/invoices';
import { HandleInvoiceTransitionArgs } from '../InvoiceWorkflow';
import { useInvoice } from './useInvoice';
import { useInvoiceFlow } from './useInvoiceFlow';
import { DIALOG_560 } from 'components/modals/dialogWidths';

type InvoiceTransition = {
    to: InvoiceStatusType;
    handleTransition: () => void;
};

type PossibleInvoiceTransitions = {
    main?: InvoiceTransition;
    secondary?: InvoiceTransition;
};

type InvoiceTransitionActions = {
    openPostponeModal: () => void;
    openRejectModal: (to: InvoiceStatusType) => void;
    possibleTransitions: PossibleInvoiceTransitions;
    isLoading: boolean;
};

export const useInvoiceTransition = (
    invoice: InvoiceData,
    handleSubmit: UseFormHandleSubmit<InvoiceForm>,
    _templateValidation: () => Promise<boolean>,
    onPostponeSuccess?: () => void
): InvoiceTransitionActions => {
    const posthog = usePostHog();
    const [updateInvoiceStatus, { isLoading: transitionRequestLoading }] = useUpdateInvoiceStatusMutation();
    const { openModal, closeModal } = useModal();
    const { isCredit, isDebit, isCorrection } = useInvoice(invoice);
    const { isRequestedToPostpone } = useInvoiceFlow(invoice);

    const handleStatusChange = useCallback(
        async (args: HandleInvoiceTransitionArgs) => {
            await handleSubmit(() =>
                updateInvoiceStatus({
                    id: invoice.id,
                    invoiceStatusTypeCode: args.to,
                    transitionActionTypeCode: args.transactionActionType,
                    transitionComment: args.comment,
                }).then((result) => {
                    if (!('error' in result)) {
                        posthog.capture('invoice_transitioned', {
                            action: args.transactionActionType,
                            from_status: invoice.invoiceStatusTypeCode,
                            to_status: args.to,
                        });
                    }
                })
            )();
        },
        [invoice, handleSubmit, updateInvoiceStatus, posthog]
    );

    const { openSendInvoicesModal } = useSendInvoicesModal({
        invoices: [invoice],
        emailTemplateType: InvoiceEmailTemplateType.INVOICE,
        transitionToSent: true,
    });

    const handleInvoiceReject = useCallback(
        async (toStatus: InvoiceStatusType, reason: string) =>
            handleStatusChange({
                to: toStatus,
                transactionActionType: InvoiceTransactionActionType.REJECTED,
                comment: reason,
            }),
        [handleStatusChange]
    );

    const handleDisagreeAndContinue = useCallback(
        async (reason: string) =>
            handleStatusChange({
                to: InvoiceStatusType.REVIEWED,
                transactionActionType: InvoiceTransactionActionType.REJECTED,
                comment: reason,
            }),
        [handleStatusChange]
    );

    const openRejectModal = useCallback(
        (to: InvoiceStatusType): void => {
            return openModal(
                <InvoiceWorkflowRejectModal
                    invoice={invoice}
                    onClose={closeModal}
                    onReject={(reason) => handleInvoiceReject(to, reason)}
                    onDisagreeAndContinue={(reason) => handleDisagreeAndContinue(reason)}
                    rejectToState={to}
                    isRequestedToPostpone={isRequestedToPostpone}
                />,
                closeModal,
                undefined,
                undefined,
                DIALOG_560
            );
        },
        [openModal, invoice, closeModal, isRequestedToPostpone, handleInvoiceReject, handleDisagreeAndContinue]
    );

    const openRequestToPostponeModal = useCallback((): void => {
        return openModal(
            <InvoicePostponeFormModal
                invoice={invoice}
                onClose={closeModal}
                isRequesting
                handleStatusChange={handleStatusChange}
            />,
            closeModal,
            undefined,
            undefined,
            DIALOG_560
        );
    }, [openModal, invoice, closeModal, handleStatusChange]);

    const openPostponeModal = useCallback((): void => {
        return openModal(
            <InvoicePostponeFormModal
                invoice={invoice}
                onClose={closeModal}
                onSuccess={onPostponeSuccess}
                reason={isRequestedToPostpone ? invoice.invoiceTransitions[0].comment : ''}
                handleStatusChange={handleStatusChange}
            />,
            closeModal,
            undefined,
            undefined,
            DIALOG_560
        );
    }, [openModal, invoice, closeModal, onPostponeSuccess, isRequestedToPostpone, handleStatusChange]);

    let possibleTransitions: PossibleInvoiceTransitions = {};

    switch (invoice.invoiceStatusTypeCode) {
        case InvoiceStatusType.DRAFT:
            possibleTransitions =
                isCredit || isCorrection
                    ? {
                          main: {
                              to: InvoiceStatusType.FINALIZED,
                              handleTransition: () => {
                                  void handleStatusChange({
                                      to: InvoiceStatusType.FINALIZED,
                                      transactionActionType: InvoiceTransactionActionType.APPROVED,
                                  });
                              },
                          },
                      }
                    : {
                          main: {
                              to: InvoiceStatusType.REVIEWED,
                              handleTransition: () => {
                                  void handleStatusChange({
                                      to: InvoiceStatusType.REVIEWED,
                                      transactionActionType: InvoiceTransactionActionType.APPROVED,
                                  });
                              },
                          },
                      };

            if (isDebit) {
                possibleTransitions = {
                    ...possibleTransitions,
                    secondary: {
                        to: InvoiceStatusType.POSTPONED,
                        handleTransition: () => openRequestToPostponeModal(),
                    },
                };
            }

            break;
        case InvoiceStatusType.REVIEWED:
            possibleTransitions = isCredit
                ? { main: undefined, secondary: undefined }
                : {
                      main: {
                          to: isRequestedToPostpone ? InvoiceStatusType.POSTPONED : InvoiceStatusType.APPROVED,
                          handleTransition: () => {
                              if (isRequestedToPostpone) {
                                  openPostponeModal();
                              } else {
                                  void handleStatusChange({
                                      to: InvoiceStatusType.APPROVED,
                                      transactionActionType: InvoiceTransactionActionType.APPROVED,
                                  });
                              }
                          },
                      },
                      secondary: {
                          to: InvoiceStatusType.DRAFT,
                          handleTransition: () => openRejectModal(InvoiceStatusType.DRAFT),
                      },
                  };
            break;
        case InvoiceStatusType.APPROVED:
            possibleTransitions = isCredit
                ? { main: undefined, secondary: undefined }
                : {
                      main: {
                          to: InvoiceStatusType.FINALIZED,
                          handleTransition: () => {
                              void handleStatusChange({
                                  to: InvoiceStatusType.FINALIZED,
                                  transactionActionType: InvoiceTransactionActionType.APPROVED,
                              });
                          },
                      },
                      secondary: {
                          to: InvoiceStatusType.REVIEWED,
                          handleTransition: () => openRejectModal(InvoiceStatusType.REVIEWED),
                      },
                  };
            break;
        case InvoiceStatusType.FINALIZED:
            possibleTransitions = {
                main: {
                    to: InvoiceStatusType.SENT,
                    handleTransition: () => openSendInvoicesModal(),
                },
                secondary:
                    isCredit || isCorrection
                        ? {
                              to: InvoiceStatusType.DRAFT,
                              handleTransition: () => openRejectModal(InvoiceStatusType.DRAFT),
                          }
                        : {
                              to: InvoiceStatusType.APPROVED,
                              handleTransition: () => openRejectModal(InvoiceStatusType.APPROVED),
                          },
            };
            break;
        case InvoiceStatusType.PAID:
            possibleTransitions = { main: undefined, secondary: undefined };
            break;
    }

    return {
        openPostponeModal,
        openRejectModal,
        possibleTransitions,
        isLoading: transitionRequestLoading,
    };
};
